import { useTranslation } from "react-i18next";
import { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { ChevronRight, Sparkles, Camera, Target, BarChart3, Zap, LayoutDashboard, AlertTriangle, Pencil, MessageCircle, Rss } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useIsMobile } from "@/hooks/use-mobile";
import { openInExternalBrowser } from "@/lib/platform";
import { ENABLE_COMMITMENT_PLEDGES } from "@/config/features";

// Helper function to add timestamps to logs
const ts = () => new Date().toISOString();

interface TutorialStepProps {
  onComplete: () => void;
}

export const TutorialStep = ({
 onComplete }: TutorialStepProps) => {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(0);
  const [direction, setDirection] = useState(1);
  const isMobile = useIsMobile();

  useEffect(() => {
    console.log(`[${ts()}] [TutorialStep] Component mounted`);
    return () => console.log(`[${ts()}] [TutorialStep] Component unmounting`);
  }, []);

  useEffect(() => {
    console.log(`[${ts()}] [TutorialStep] Current page changed to:`, currentPage, 'Title:', pages[currentPage]?.title);
  }, [currentPage]);

  const allPages = [
    {
      icon: Zap,
      title: "Quick Logging",
      value: "Update totals easily",
      bullets: [
        "🌈 Not just food.. Anything you want",
        "⭐ Start with `I want to track [ANYTHING]`",
        "💬 Then, say what you did or ate",
        "📷 or snap a pic!"
      ],
      content: <QuickEditPage />,
      gradient: "from-amber-500/10 via-orange-500/10 to-red-500/10",
    },
    {
      icon: BarChart3,
      title: "Your Metrics",
      value: "Macros, activities, anything",
      bullets: [
        "🎨 Use AI to get started, or pick & create yourself",
      ],
      content: <MetricsPreviewPage />,
      gradient: "from-blue-500/10 via-cyan-500/10 to-teal-500/10",
    },
    {
      icon: Target,
      title: "Set Targets",
      value: "For your charts",
      bullets: [
        "🌎 Charts show on your profile",
        "❤️ You can react to other users' charts",
        "🔥 Follow friends to know when they're on a streak!",
        "🔒 Update profile visibility in settings/profile"
      ],
      content: <GoalsVisualizationPage />,
      gradient: "from-purple-500/10 via-pink-500/10 to-rose-500/10",
    },
    {
      icon: AlertTriangle,
      title: "Set Consequences",
      value: "High-stakes for serious commitment",
      bullets: [
      ],
      content: <PenaltiesPage />,
      gradient: "from-red-500/10 via-orange-500/10 to-yellow-500/10",
    },
  ];

  // Filter pages based on feature flags
  const pages = ENABLE_COMMITMENT_PLEDGES ? allPages : allPages.filter(page => page.title !== "Set Consequences");

  const handleNext = () => {
    console.log(`[${ts()}] [TutorialStep] handleNext called, currentPage:`, currentPage);
    if (currentPage < pages.length - 1) {
      setDirection(1);
      const nextPage = currentPage + 1;
      console.log(`[${ts()}] [TutorialStep] Moving to page:`, nextPage, 'Title:', pages[nextPage].title);
      // Add a slight delay between page clearing and transitioning to next page
      setTimeout(() => {
        setCurrentPage(nextPage);
      }, 150);
    } else {
      console.log(`[${ts()}] [TutorialStep] Tutorial complete, calling onComplete`);
      onComplete();
    }
  };

  const progressValue = ((currentPage + 1) / pages.length) * 100;

  const slideVariants = {
    enter: (direction: number) => {
      console.log(`[${ts()}] [TutorialStep] Slide entering, direction:`, direction);
      return {
        x: direction > 0 ? 50 : -50,
        opacity: 0,
        scale: 0.95,
        rotateY: direction > 0 ? 10 : -10,
      };
    },
    center: {
      x: 0,
      opacity: 1,
      scale: 1,
      rotateY: 0,
    },
    exit: (direction: number) => {
      console.log(`[${ts()}] [TutorialStep] Slide exiting, direction:`, direction);
      return {
        x: direction > 0 ? -50 : 50,
        opacity: 0,
        scale: 0.95,
        rotateY: direction > 0 ? -10 : 10,
      };
    },
  };

  return (
    <motion.div 
      className="w-full max-w-4xl mx-auto flex flex-col h-full select-none"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
    >
      {/* Animated gradient background */}
      <motion.div
        className={`absolute inset-0 bg-gradient-to-br ${pages[currentPage].gradient} -z-10 rounded-lg`}
        onAnimationStart={() => console.log(`[${ts()}] [TutorialStep] Background gradient animation started for page:`, currentPage)}
        onAnimationComplete={() => console.log(`[${ts()}] [TutorialStep] Background gradient animation completed for page:`, currentPage)}
        animate={{
          opacity: [0.3, 0.5, 0.3],
        }}
        transition={{
          duration: 4,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />

      <div className={isMobile ? "space-y-2 px-4 pt-4 pb-4 flex-shrink-0" : "space-y-2 px-4 pt-4 pb-0 flex-shrink-0"}>
        <Progress value={progressValue} className="h-2" />
        <motion.p
          key={currentPage}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          onAnimationStart={() => console.log(`[${ts()}] [TutorialStep] Progress text animation started`)}
          onAnimationComplete={() => console.log(`[${ts()}] [TutorialStep] Progress text animation completed`)}
          className="text-sm text-muted-foreground text-center"
        >
          {currentPage + 1} of {pages.length}
        </motion.p>
      </div>

      <AnimatePresence mode="popLayout" custom={direction}>
        <motion.div
          key={currentPage}
          custom={direction}
          variants={slideVariants}
          initial="enter"
          animate="center"
          exit="exit"
          onAnimationStart={() => console.log(`[${ts()}] [TutorialStep] Page animation started for page:`, currentPage, pages[currentPage].title)}
          onAnimationComplete={() => console.log(`[${ts()}] [TutorialStep] Page animation completed for page:`, currentPage, pages[currentPage].title)}
          transition={{
            duration: 0.5,
            ease: [0.4, 0, 0.2, 1],
          }}
          className={isMobile ? "flex flex-col flex-1 min-h-0 justify-center pb-20" : "flex flex-col flex-1 min-h-0"}
        >
          <div className="text-center space-y-2 px-4 py-3 flex-shrink-0">
            {/* Icon with floating animation */}
            <motion.div
              className={isMobile ? "flex justify-center mb-2 pt-2" : "flex justify-center mb-2 pt-6 sm:pt-8"}
              initial={{ opacity: 0 }}
              animate={{
                opacity: 1,
                y: [0, -10, 0],
              }}
              transition={{
                opacity: { duration: 0.3, delay: 0 },
                y: {
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 0.3,
                },
              }}
            >
              {(() => {
                const IconComponent = pages[currentPage].icon;
                return (
                  <motion.div
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{ scale: 1, rotate: 0 }}
                    onAnimationStart={() => console.log(`[${ts()}] [TutorialStep] Icon animation started for page:`, currentPage)}
                    onAnimationComplete={() => console.log(`[${ts()}] [TutorialStep] Icon animation completed for page:`, currentPage)}
                    transition={{
                      type: "spring",
                      stiffness: 200,
                      damping: 15,
                      delay: 0,
                    }}
                  >
                    <IconComponent className={isMobile ? "h-10 w-10 text-accent drop-shadow-lg" : "h-12 w-12 text-accent drop-shadow-lg"} />
                  </motion.div>
                );
              })()}
            </motion.div>
            
            {/* Title with slide in */}
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              onAnimationStart={() => console.log(`[${ts()}] [TutorialStep] Title animation started for page:`, currentPage)}
              onAnimationComplete={() => console.log(`[${ts()}] [TutorialStep] Title animation completed for page:`, currentPage)}
              transition={{ delay: 0.15, duration: 0.3 }}
              className={isMobile ? "text-xl font-bold" : "text-3xl font-bold"}
            >
              {pages[currentPage].title}
            </motion.h2>
            
            {/* Value Statement with slide in */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              onAnimationStart={() => console.log(`[${ts()}] [TutorialStep] Value statement animation started for page:`, currentPage)}
              onAnimationComplete={() => console.log(`[${ts()}] [TutorialStep] Value statement animation completed for page:`, currentPage)}
              transition={{ delay: 0.3, duration: 0.3 }}
              className={isMobile ? "text-sm text-accent font-medium" : "text-base text-accent font-medium"}
            >
              {pages[currentPage].value}
            </motion.p>
            
            {/* Bullet Points with staggered animation */}
            <motion.div
              initial="hidden"
              animate="visible"
              onAnimationStart={() => console.log(`[${ts()}] [TutorialStep] Bullets animation started for page:`, currentPage)}
              onAnimationComplete={() => console.log(`[${ts()}] [TutorialStep] Bullets animation completed for page:`, currentPage)}
              className={isMobile ? "text-xs text-left max-w-sm mx-auto pt-2" : "text-sm text-left max-w-md mx-auto pt-2"}
            >
              <ul className="space-y-1.5 text-muted-foreground">
                {pages[currentPage].bullets.map((bullet, idx) => (
                  <motion.li
                    key={idx}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{
                      delay: 0.45 + idx * 0.7,
                      duration: 0.3,
                    }}
                    className="flex items-start gap-2"
                  >
                    <motion.span
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{
                        delay: 0.45 + idx * 0.7 + 0.1,
                        type: "spring",
                        stiffness: 300,
                      }}
                      className="text-accent mt-0.5"
                    >
                      •
                    </motion.span>
                    <span>{bullet}</span>
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            onAnimationStart={() => console.log(`[${ts()}] [TutorialStep] Card content animation started for page:`, currentPage)}
            onAnimationComplete={() => console.log(`[${ts()}] [TutorialStep] Card content animation completed for page:`, currentPage)}
            transition={{ delay: 0.45 + pages[currentPage].bullets.length * 0.7 + 0.3, duration: 0.4 }}
            className={isMobile ? "px-4 py-2" : "flex-1 overflow-y-auto px-4 py-2 min-h-0"}
          >
            <Card className={isMobile ? "p-4 shadow-xl overflow-visible" : "p-8 shadow-xl overflow-visible"}>
              {pages[currentPage].content}
            </Card>
          </motion.div>

          {!isMobile && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="px-4 pb-6 flex-shrink-0"
            >
              <Button
                onClick={handleNext}
                size="lg"
                className="w-full group relative overflow-hidden"
              >
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                  animate={{
                    x: ["-100%", "100%"],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "linear",
                  }}
                />
                {currentPage < pages.length - 1 ? (
                  <>
                    Next{" "}
                    <motion.div
                      animate={{ x: [0, 5, 0] }}
                      transition={{
                        duration: 1.5,
                        repeat: Infinity,
                        ease: "easeInOut",
                      }}
                    >
                      <ChevronRight className="ml-2 h-4 w-4" />
                    </motion.div>
                  </>
                ) : (
                  <motion.span
                    animate={{ scale: [1, 1.05, 1] }}
                    transition={{
                      duration: 1.5,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                  >{t('ui.text.get_started')}</motion.span>
                )}
              </Button>
            </motion.div>
          )}
        </motion.div>
      </AnimatePresence>

      {isMobile && (
        <motion.div
          initial={{ y: 100 }}
          animate={{ y: 0 }}
          transition={{ delay: 0.5 }}
          className="fixed bottom-0 left-0 right-0 px-4 py-4 pb-8 border-t bg-background z-10"
        >
          <Button
            onClick={handleNext}
            size="lg"
            className="w-full max-w-4xl mx-auto group relative overflow-hidden"
          >
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
              animate={{
                x: ["-100%", "100%"],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "linear",
              }}
            />
            {currentPage < pages.length - 1 ? (
              <>
                Next{" "}
                <motion.div
                  animate={{ x: [0, 5, 0] }}
                  transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                >
                  <ChevronRight className="ml-2 h-4 w-4" />
                </motion.div>
              </>
            ) : (
              <motion.span
                animate={{ scale: [1, 1.05, 1] }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >{t('ui.text.get_started')}</motion.span>
            )}
          </Button>
        </motion.div>
      )}
    </motion.div>
  );
};

// --- Page 1: Metrics Preview ---
const MetricsPreviewPage = () => {
  const { t } = useTranslation();
  console.log(`[${ts()}] [MetricsPreviewPage] Component rendered`);
  const [currentCycle, setCurrentCycle] = useState(0);
  
  // Define different sets of 4 metrics to cycle through
  // Each set includes 3 built-in metrics + 1 custom metric
  const metricCycles = [
    [
      { emoji: "🔥", name: "Calories", isCustom: false, isSelected: true },
      { emoji: "🍗", name: "Protein", isCustom: false, isSelected: false },
      { emoji: "💪", name: "Gym Time", isCustom: false, isSelected: true },
      { emoji: "📚", name: "Study Hours", isCustom: true, isSelected: false },
    ],
    [
      { emoji: "💧", name: "Water", isCustom: false, isSelected: false },
      { emoji: "😴", name: "Sleep", isCustom: false, isSelected: true },
      { emoji: "🏃", name: "Run Distance", isCustom: false, isSelected: false },
      { emoji: "🧘", name: "Meditation", isCustom: true, isSelected: true },
    ],
    [
      { emoji: "🌾", name: "Fiber", isCustom: false, isSelected: true },
      { emoji: "⚖️", name: "Body Weight", isCustom: false, isSelected: false },
      { emoji: "😊", name: "Mood", isCustom: false, isSelected: true },
      { emoji: "💻", name: "Code Hours", isCustom: true, isSelected: false },
    ],
    [
      { emoji: "🍞", name: "Carbs", isCustom: false, isSelected: false },
      { emoji: "✨", name: "Appearance", isCustom: false, isSelected: true },
      { emoji: "📊", name: "Body Fat", isCustom: false, isSelected: false },
      { emoji: "🎨", name: "Art Time", isCustom: true, isSelected: true },
    ],
  ];
  
  // Cycle through different metric sets every 2.5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentCycle((prev) => (prev + 1) % metricCycles.length);
    }, 2500);
    return () => clearInterval(interval);
  }, [metricCycles.length]);
  
  const currentMetrics = metricCycles[currentCycle];
  
  return (
    <div className="space-y-3 w-full h-full flex flex-col justify-center overflow-visible py-4">
      <div className="relative max-w-xs mx-auto w-full px-2">
        <div className="grid grid-cols-2 gap-3 sm:gap-4">
          {currentMetrics.map((metric, idx) => (
            <motion.div
              key={`${currentCycle}-${idx}`}
              initial={{ opacity: 0, scale: 0.8, y: 20 }}
              animate={{ 
                opacity: 1,
                scale: 1,
                y: 0,
              }}
              exit={{ opacity: 0, scale: 0.8, y: -20 }}
              onAnimationStart={() => {
                if (idx === 0) {
                  console.log(`[${ts()}] [MetricsPreviewPage] First metric animation started, cycle:`, currentCycle);
                }
              }}
              onAnimationComplete={() => {
                if (idx === currentMetrics.length - 1) {
                  console.log(`[${ts()}] [MetricsPreviewPage] Last metric animation completed, cycle:`, currentCycle);
                }
              }}
              transition={{
                duration: 0.5,
                delay: idx * 0.1,
                type: "spring",
                stiffness: 200,
              }}
              className="transform-gpu"
            >
              <Card className={`p-2 sm:p-3 text-center transition-all cursor-pointer relative overflow-visible ${
                metric.isSelected 
                  ? 'border-accent/60 bg-gradient-to-br from-background to-accent/15 shadow-md' 
                  : 'border-border/40 bg-muted/30 opacity-60'
              }`}>
                {metric.isCustom && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: idx * 0.1 + 0.3 }}
                    className="absolute top-0.5 right-0.5 bg-purple-500/20 border border-purple-500/30 rounded-full px-1 py-0.5 z-10"
                  >
                    <span className="text-[8px] font-semibold text-purple-500">{t('ui.text.custom')}</span>
                  </motion.div>
                )}
                <motion.div
                  className="text-xl sm:text-2xl mb-0.5 will-change-transform"
                  animate={metric.isSelected ? {
                    scale: [1, 1.15, 1],
                  } : {}}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: idx * 0.2,
                  }}
                >
                  {metric.emoji}
                </motion.div>
                <div className="text-xs sm:text-sm font-medium">
                  {metric.name}
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

const MetricDisplayStep = () => {
  const { t } = useTranslation();
  const [currentCycle, setCurrentCycle] = useState(0);
  const metricCycles = [
    [
      { emoji: "🏃", name: "Steps", isCustom: false },
      { emoji: "💧", name: "Water", isCustom: false },
      { emoji: "🥗", name: "Healthy Eating", isCustom: false },
      { emoji: "😊", name: "Mood", isCustom: false },
    ],
    [
      { emoji: "💤", name: "Sleep", isCustom: false },
      { emoji: "🏋️", name: "Workout", isCustom: false },
      { emoji: "📖", name: "Reading", isCustom: true },
      { emoji: "🧘", name: "Meditation", isCustom: true },
    ],
    [
      { emoji: "⚖️", name: "Weight", isCustom: false },
      { emoji: "📊", name: "Body Fat", isCustom: false },
      { emoji: "🎨", name: "Art Time", isCustom: true },
      { emoji: "🎸", name: "Music", isCustom: true },
    ],
  ];
  
  // Cycle through different metric sets every 2.5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentCycle((prev) => (prev + 1) % metricCycles.length);
    }, 2500);
    return () => clearInterval(interval);
  }, [metricCycles.length]);
  
  const currentMetrics = metricCycles[currentCycle];
  
  return (
    <div className="space-y-3 w-full h-full flex flex-col justify-center overflow-hidden">
      <div className="relative max-w-md mx-auto w-full">
        <div className="grid grid-cols-2 gap-3 sm:gap-4">
          {currentMetrics.map((metric, idx) => (
            <motion.div
              key={`${currentCycle}-${idx}`}
              initial={{ opacity: 0, scale: 0.8, y: 20 }}
              animate={{ 
                opacity: 1,
                scale: 1,
                y: 0,
              }}
              exit={{ opacity: 0, scale: 0.8, y: -20 }}
              transition={{
                duration: 0.5,
                delay: idx * 0.1,
                type: "spring",
                stiffness: 200,
              }}
            >
              <Card className={`p-3 sm:p-4 text-center transition-all cursor-pointer border-accent/40 bg-gradient-to-br from-background to-accent/10 shadow-md relative overflow-hidden`}>
                {metric.isCustom && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: idx * 0.1 + 0.3 }}
                    className="absolute top-1 right-1 bg-purple-500/20 border border-purple-500/30 rounded-full px-1.5 py-0.5"
                  >
                    <span className="text-[9px] font-semibold text-purple-500">{t('ui.text.custom')}</span>
                  </motion.div>
                )}
                <motion.div
                  className="text-2xl sm:text-3xl mb-1"
                  animate={{
                    scale: [1, 1.15, 1],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: idx * 0.2,
                  }}
                >
                  {metric.emoji}
                </motion.div>
                <div className="text-sm sm:text-base font-medium">
                  {metric.name}
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

// --- Page 2: Goals Visualization ---
const GoalsVisualizationPage = () => {
  const { t } = useTranslation();
  const renderStart = Date.now();
  console.log(`[${ts()}] [GoalsVisualizationPage] Component render started`);
  
  const days = Array.from({ length: 7 }, (_, i) => i);
  const weeks = Array.from({ length: 15 }, (_, i) => i); // Reduced from 7 weeks to ~15 weeks (about 1/3)
  
  console.log(`[${ts()}] [GoalsVisualizationPage] Arrays created (${weeks.length} weeks × ${days.length} days = ${weeks.length * days.length} cells), took ${Date.now() - renderStart}ms`);

  const getIntensity = (week: number, day: number) => {
    const value = (week * 7 + day + Math.random() * 30) % 110;
    if (value >= 100) return 4;
    if (value >= 75) return 3;
    if (value >= 50) return 2;
    if (value >= 25) return 1;
    return 0;
  };

  const getColor = (intensity: number) => {
    const colors = [
      "bg-[hsl(var(--contrib-0))]",
      "bg-[hsl(var(--contrib-1))]",
      "bg-[hsl(var(--contrib-2))]",
      "bg-[hsl(var(--contrib-3))]",
      "bg-[hsl(var(--contrib-4))]",
    ];
    return colors[intensity];
  };
  
  // Pre-compute all cell data to avoid calculating during render
  const cellData = useMemo(() => {
    const computeStart = Date.now();
    const data = weeks.map(week => 
      days.map(day => {
        const intensity = getIntensity(week, day);
        return {
          intensity,
          color: getColor(intensity),
          delay: (week * 7 + day) * 0.001
        };
      })
    );
    console.log(`[${ts()}] [GoalsVisualizationPage] Cell data pre-computed, took ${Date.now() - computeStart}ms`);
    return data;
  }, [weeks.length, days.length]);
  
  console.log(`[${ts()}] [GoalsVisualizationPage] Component render returning JSX, took ${Date.now() - renderStart}ms`);

  return (
    <div className="space-y-4 w-full h-full flex flex-col justify-center">
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        onAnimationStart={() => console.log(`[${ts()}] [GoalsVisualizationPage] Container animation started`)}
        onAnimationComplete={() => console.log(`[${ts()}] [GoalsVisualizationPage] Container animation completed`)}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="flex gap-1 justify-center will-change-transform"
      >
        {weeks.map((week) => (
          <div key={week} className="flex flex-col gap-1">
            {days.map((day) => {
              const cell = cellData[week][day];
              return (
                <motion.div
                  key={day}
                  initial={{ scale: 0, rotate: -90 }}
                  animate={{ scale: 1, rotate: 0 }}
                  onAnimationStart={() => {
                    if (week === 0 && day === 0) {
                      console.log(`[${ts()}] [GoalsVisualizationPage] First cell animation started`);
                    }
                  }}
                  onAnimationComplete={() => {
                    if (week === weeks.length - 1 && day === days.length - 1) {
                      console.log(`[${ts()}] [GoalsVisualizationPage] Last cell animation completed`);
                    }
                  }}
                  whileHover={{
                    scale: 1.3,
                    zIndex: 10,
                    boxShadow: "0 0 15px rgba(var(--accent), 0.6)",
                  }}
                  transition={{
                    delay: cell.delay,
                    duration: 0.2,
                    type: "spring",
                    stiffness: 400,
                    damping: 25,
                  }}
                  className={`w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-sm ${cell.color} cursor-pointer will-change-transform transform-gpu`}
                />
              );
            })}
          </div>
        ))}
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        onAnimationStart={() => console.log(`[${ts()}] [GoalsVisualizationPage] Legend animation started`)}
        onAnimationComplete={() => console.log(`[${ts()}] [GoalsVisualizationPage] Legend animation completed`)}
        transition={{ delay: 0.2 }}
        className="flex items-center justify-center gap-2 text-xs"
      >
        <span className="text-muted-foreground">{t('ui.text.not_met')}</span>
        {[0, 1, 2, 3, 4].map((i) => (
          <motion.div
            key={i}
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{
              delay: 0.25 + i * 0.03,
              type: "spring",
              stiffness: 350,
            }}
            whileHover={{ scale: 1.5 }}
            className={`w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-sm ${getColor(i)}`}
          />
        ))}
        <span className="text-muted-foreground">{t('ui.text.goal_met')}</span>
      </motion.div>
    </div>
  );
};

// --- Page 3: Quick Edit ---
const QuickEditPage = () => {
  const { t } = useTranslation();
  console.log(`[${ts()}] [QuickEditPage] Component rendered`);
  const [currentExample, setCurrentExample] = useState(0);
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [showOutputs, setShowOutputs] = useState(false);

  // Metric circles for manual entry demonstration
  const metricCircles = [
    { emoji: "🔥", name: "Calories", value: "2,100" },
    { emoji: "🍗", name: "Protein", value: "145g" },
    { emoji: "🏃", name: "Miles", value: "3.2" },
    { emoji: "💪", name: "Gym", value: "45m" },
    { emoji: "😴", name: "Sleep", value: "7.5h" },
    { emoji: "💧", name: "Water", value: "0.6g" },
  ];

  const examples = [
    {
      type: "food",
      input: "add 2 servings",
      hasImage: true,
      imageEmoji: "🍎",
      scanning: true,
      outputs: [
        { label: "Fiber", value: "5g", icon: "🌾" },
        { label: "Calories", value: "190", icon: "🔥" },
      ],
      healthierOption: "Pears have more fiber & less sugar",
    },
    {
      type: "manual",
      input: "",
      hasImage: false,
      scanning: false,
      outputs: [],
      isManualEntry: true,
    },
    {
      type: "food",
      input: "Large bowl of oatmeal with berries",
      hasImage: false,
      scanning: false,
      outputs: [
        { label: "Fiber", value: "8g", icon: "🌾" },
        { label: "Carbs", value: "54g", icon: "🍞" },
        { label: "Calories", value: "320", icon: "🔥" },
      ],
      healthierOption: "Pears have more fiber and less sugar",
    },
    {
      type: "activity",
      input: "5k run",
      hasImage: false,
      scanning: false,
      outputs: [
        { label: "Run Distance", value: "3.1 mi", icon: "🏃" },
      ],
    },
    {
      type: "activity",
      input: "1 hr at gym",
      hasImage: false,
      scanning: false,
      outputs: [
        { label: "Gym Time", value: "60 min", icon: "💪" },
      ],
    },
    {
      type: "wellness",
      input: "\"slept 8 hours\"",
      hasImage: false,
      scanning: false,
      outputs: [
        { label: "Sleep", value: "8.0 hrs", icon: "😴" },
      ],
    },
    {
      type: "wellness",
      input: "3/4 a gallon of water",
      hasImage: false,
      scanning: false,
      outputs: [
        { label: "Water", value: "0.75 gal", icon: "💧" },
      ],
    },
    {
      type: "results",
      input: "175 pounds",
      hasImage: false,
      scanning: false,
      outputs: [
        { label: "Body Weight", value: "175 lbs", icon: "⚖️" },
      ],
    },
    {
      type: "results",
      input: "Feeling great today! Mood: 9 😊",
      hasImage: false,
      scanning: false,
      outputs: [
        { label: "Mood", value: "9", icon: "😊" },
      ],
    },
  ];

  // Auto-cycle through examples
  useEffect(() => {
    console.log(`[${ts()}] [QuickEditPage] Setting up example cycle interval`);
    // Reset outputs when example changes
    setShowOutputs(false);
    
    const interval = setInterval(() => {
      setCurrentExample((prev) => {
        const next = (prev + 1) % examples.length;
        console.log(`[${ts()}] [QuickEditPage] Cycling to example:`, next);
        return next;
      });
    }, 5000); // Reduced from 7s to 5s for faster cycling
    return () => {
      console.log(`[${ts()}] [QuickEditPage] Cleaning up example cycle interval`);
      clearInterval(interval);
    };
  }, [examples.length]);

  // Show outputs after appropriate delay for each example type
  useEffect(() => {
    const current = examples[currentExample];
    console.log(`[${ts()}] [QuickEditPage] Example changed to:`, currentExample, 'Type:', current.type, 'HasImage:', current.hasImage);
    if (current.isManualEntry) {
      console.log(`[${ts()}] [QuickEditPage] Manual entry example - no outputs to show`);
      // No outputs to show for manual entry
      return;
    }
    const delay = current.hasImage ? 1900 : 300; // Reduced from 3800ms to 1900ms (half) for image scan, 600ms to 300ms for text
    console.log(`[${ts()}] [QuickEditPage] Setting timeout to show outputs in`, delay, 'ms');
    
    const timer = setTimeout(() => {
      console.log(`[${ts()}] [QuickEditPage] Showing outputs now`);
      setShowOutputs(true);
    }, delay);
    
    return () => {
      console.log(`[${ts()}] [QuickEditPage] Cleaning up output timer`);
      clearTimeout(timer);
    };
  }, [currentExample]);

  const current = examples[currentExample];

  return (
    <div className="space-y-3 sm:space-y-4 w-full max-w-md mx-auto">
      {current.isManualEntry ? (
        // Manual Entry via Circles
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4 }}
            className="relative overflow-hidden h-20 flex items-center"
          >
            <motion.div
              animate={{
                x: [0, -100],
              }}
              transition={{
                duration: 15,
                repeat: Infinity,
                ease: "linear",
              }}
              className="flex gap-4 absolute left-0"
            >
              {[...metricCircles, ...metricCircles, ...metricCircles].map((metric, idx) => (
                <motion.div
                  key={idx}
                  whileHover={{ scale: 1.1, y: -5 }}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{
                    delay: idx * 0.05,
                    type: "spring",
                    stiffness: 100,
                    damping: 15,
                  }}
                  className="flex-shrink-0 w-20 h-20 rounded-full bg-gradient-to-br from-accent/20 to-accent/10 border-2 border-accent/30 shadow-md flex flex-col items-center justify-center cursor-pointer hover:border-accent hover:shadow-lg transition-all"
                >
                  <div className="text-2xl mb-0.5">{metric.emoji}</div>
                  <div className="text-xs font-bold text-accent">{metric.value}</div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
          
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="text-xs text-center text-muted-foreground"
          >
            ⚡ Quick-edit circles for instant updates
          </motion.p>
        </>
      ) : (
        // AI Entry
        <>
      {/* Input Section */}
      <AnimatePresence mode="wait">
        <motion.div
          key={`input-${currentExample}`}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 10 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          className="relative"
        >
          <motion.div
            animate={{
              boxShadow: [
                "0 0 0 0 rgba(34, 197, 94, 0), 0 0 6px 0 rgba(34, 197, 94, 0.2)",
                "0 0 0 6px rgba(34, 197, 94, 0.06), 0 0 12px 1px rgba(34, 197, 94, 0.25)",
                "0 0 0 0 rgba(34, 197, 94, 0), 0 0 6px 0 rgba(34, 197, 94, 0.2)",
              ],
            }}
            transition={{
              duration: 2.5,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 border rounded-lg bg-background border-green-500/20"
          >
            <motion.div
              animate={{ rotate: [0, 10, -10, 0] }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            >
              <Sparkles className="h-4 w-4 text-accent flex-shrink-0" />
            </motion.div>
            
            {current.hasImage && (
              <motion.div
                id={`camera-btn-${currentExample}`}
                animate={{ scale: [1, 1.1, 1] }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                <Camera className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              </motion.div>
            )}
            
            <motion.span
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="text-xs sm:text-sm text-muted-foreground flex-1"
            >
              {current.input}
            </motion.span>
          </motion.div>
        </motion.div>
      </AnimatePresence>

      {/* Image Scan and Output Layout - Side by Side */}
      {current.hasImage ? (
        <AnimatePresence mode="wait">
          <motion.div
            key={`scan-output-${currentExample}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="flex gap-3 sm:gap-4 items-start"
          >
            {/* Image with Scanning Animation */}
            <motion.div
              className="relative flex-shrink-0"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              <motion.div
                className="relative w-24 h-24 sm:w-32 sm:h-32 rounded-2xl bg-gradient-to-br from-accent/20 via-accent/10 to-accent/5 border-2 border-accent/30 flex items-center justify-center overflow-hidden"
                initial={{ 
                  scale: 0.3,
                  x: -120,
                  y: -80,
                }}
                animate={{ 
                  scale: 1,
                  x: 0,
                  y: 0,
                }}
                transition={{
                  duration: 0.6,
                  delay: 0.6,
                  type: "spring",
                  stiffness: 180,
                  damping: 12,
                }}
              >
                {/* Scanning Bar - One complete cycle (2 swipes: down then up) with exponential acceleration */}
                <motion.div
                  className="absolute inset-0 pointer-events-none"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: [0, 1, 1, 0] }}
                  transition={{ 
                    duration: 1.2,
                    delay: 0.65,
                    times: [0, 0.05, 0.95, 1],
                  }}
                >
                  <motion.div
                    className="absolute left-0 right-0 h-1 bg-gradient-to-r from-transparent via-accent to-transparent"
                    style={{ boxShadow: "0 0 20px rgba(var(--accent), 0.8)" }}
                    animate={{
                      top: ["0%", "100%", "0%"],
                    }}
                    transition={{
                      duration: 1.2,
                      delay: 0.65,
                      ease: [0.22, 0.61, 0.36, 1], // Custom cubic-bezier for exponential feel
                      times: [0, 0.5, 1],
                    }}
                  />
                </motion.div>
                
                {/* Image/Emoji */}
                <motion.div
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{
                    delay: 1.0,
                    type: "spring",
                    stiffness: 200,
                    damping: 15,
                  }}
                  className="text-5xl sm:text-6xl z-10"
                >
                  {current.imageEmoji}
                </motion.div>

                {/* Corner Brackets */}
                {[
                  "top-1 left-1 border-t-2 border-l-2",
                  "top-1 right-1 border-t-2 border-r-2",
                  "bottom-1 left-1 border-b-2 border-l-2",
                  "bottom-1 right-1 border-b-2 border-r-2",
                ].map((className, idx) => (
                  <motion.div
                    key={idx}
                    className={`absolute ${className} border-accent w-4 h-4 sm:w-6 sm:h-6`}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{
                      delay: 1.2 + idx * 0.05,
                    }}
                  />
                ))}
              </motion.div>

              {/* Scanning Text */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: [0, 1, 1, 0] }}
                transition={{ 
                  duration: 1.2,
                  delay: 0.65,
                  times: [0, 0.1, 0.9, 1],
                }}
                className="text-center mt-2 sm:mt-3"
              >
                <motion.span
                  initial={{ fontSize: "1rem" }}
                  animate={{ 
                    fontSize: showOutputs ? "0.75rem" : "1rem"
                  }}
                  transition={{
                    duration: 0.3,
                    ease: "easeOut"
                  }}
                  className="text-accent font-medium"
                >{t('ui.text.analyzing')}</motion.span>
              </motion.div>
              
              {/* Optional Picture Label */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.8 }}
                className="text-center mt-1"
              >
                <span className="text-[10px] text-muted-foreground/70 italic">
                  📷 Optional Picture
                </span>
              </motion.div>
            </motion.div>

            {/* Output Metrics - Appear to the RIGHT of image after scan completes */}
            {showOutputs && (
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.4, ease: "easeOut" }}
                className="flex-1 space-y-2 min-w-0"
              >
                {current.outputs.map((output, idx) => (
                  <motion.div
                    key={`${currentExample}-${idx}`}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{
                      delay: idx * 0.1,
                      duration: 0.3,
                      ease: "easeOut",
                    }}
                    className="relative overflow-hidden p-2 sm:p-3 border rounded-lg bg-accent/10 border-accent/20 group hover:bg-accent/15 transition-colors cursor-pointer"
                  >
                    {/* Animated progress bar background */}
                    <motion.div
                      className="absolute inset-0 rounded-lg"
                      initial={{ 
                        scaleX: 0,
                        background: "linear-gradient(to right, rgb(239, 68, 68), rgb(34, 197, 94))"
                      }}
                      animate={{ 
                        scaleX: [0, 1],
                      }}
                      transition={{
                        delay: idx * 0.1 + 0.2,
                        duration: 0.8,
                        ease: "easeOut",
                      }}
                      style={{
                        transformOrigin: "left",
                        opacity: 0.15,
                      }}
                    />
                    
                    <div className="relative z-10 flex items-center justify-between">
                      <div className="flex items-center gap-2 min-w-0">
                        <motion.span
                          animate={{
                            scale: [1, 1.15, 1],
                          }}
                          transition={{
                            duration: 2,
                            repeat: Infinity,
                            delay: idx * 0.2,
                            ease: "easeInOut",
                          }}
                          className="text-base sm:text-lg flex-shrink-0"
                        >
                          {output.icon}
                        </motion.span>
                        <span className="text-xs sm:text-sm text-muted-foreground truncate">
                          {output.label}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <motion.span
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{
                            delay: idx * 0.1 + 0.5,
                            duration: 0.3,
                          }}
                          className="text-sm sm:text-base font-bold text-accent"
                        >
                          {output.value}
                        </motion.span>
                        <Pencil className="w-3 h-3 text-muted-foreground/40 group-hover:text-accent transition-colors" />
                      </div>
                    </div>
                  </motion.div>
                ))}
                
                {/* Healthier Option Suggestion */}
                {current.healthierOption && (
                  <motion.div
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                      delay: current.outputs.length * 0.1 + 0.5,
                      duration: 0.4,
                      ease: "easeOut",
                    }}
                    className="flex items-center gap-1.5 px-2 py-1.5 rounded bg-yellow-500/5 border border-yellow-500/20"
                  >
                    <span className="text-xs">💡</span>
                    <span className="text-[10px] sm:text-xs text-muted-foreground/80">
                      {current.healthierOption}
                    </span>
                  </motion.div>
                )}
              </motion.div>
            )}
          </motion.div>
        </AnimatePresence>
      ) : (
        // Text-only mode - outputs appear below input
        <AnimatePresence mode="wait">
          {showOutputs && (
            <motion.div
              key={`output-${currentExample}`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="space-y-2"
            >
              {current.outputs.map((output, idx) => (
                <motion.div
                  key={`${currentExample}-${idx}`}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{
                    delay: idx * 0.1,
                    duration: 0.3,
                    ease: "easeOut",
                  }}
                  className="relative overflow-hidden p-2 sm:p-3 border rounded-lg bg-accent/10 border-accent/20 group hover:bg-accent/15 transition-colors cursor-pointer"
                >
                  {/* Animated progress bar background */}
                  <motion.div
                    className="absolute inset-0 rounded-lg"
                    initial={{ 
                      scaleX: 0,
                      background: "linear-gradient(to right, rgb(239, 68, 68), rgb(34, 197, 94))"
                    }}
                    animate={{ 
                      scaleX: [0, 1],
                    }}
                    transition={{
                      delay: idx * 0.1 + 0.2,
                      duration: 0.8,
                      ease: "easeOut",
                    }}
                    style={{
                      transformOrigin: "left",
                      opacity: 0.15,
                    }}
                  />
                  
                  <div className="relative z-10 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <motion.span
                        animate={{
                          scale: [1, 1.15, 1],
                        }}
                        transition={{
                          duration: 2,
                          repeat: Infinity,
                          delay: idx * 0.2,
                          ease: "easeInOut",
                        }}
                        className="text-base sm:text-lg"
                      >
                        {output.icon}
                      </motion.span>
                      <span className="text-xs sm:text-sm text-muted-foreground">
                        {output.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <motion.span
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{
                          delay: idx * 0.1 + 0.5,
                          duration: 0.3,
                        }}
                        className="text-sm sm:text-base font-bold text-accent"
                      >
                        {output.value}
                      </motion.span>
                      <Pencil className="w-3 h-3 text-muted-foreground/40 group-hover:text-accent transition-colors" />
                    </div>
                  </div>
                </motion.div>
              ))}
              
              {/* Healthier Option Suggestion */}
              {current.healthierOption && (
                <motion.div
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    delay: current.outputs.length * 0.1 + 0.5,
                    duration: 0.4,
                    ease: "easeOut",
                  }}
                  className="flex items-center gap-1.5 px-2 py-1.5 rounded bg-yellow-500/5 border border-yellow-500/20"
                >
                  <span className="text-xs">💡</span>
                  <span className="text-[10px] sm:text-xs text-muted-foreground/80">
                    {current.healthierOption}
                  </span>
                </motion.div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      )}
      </>
      )}

      {/* Progress Dots */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="flex justify-center gap-1.5 pt-1"
      >
        {examples.map((_, idx) => (
          <motion.div
            key={idx}
            animate={{
              scale: currentExample === idx ? 1.2 : 1,
              backgroundColor:
                currentExample === idx
                  ? "hsl(var(--accent))"
                  : "hsl(var(--muted-foreground) / 0.3)",
            }}
            transition={{ duration: 0.3 }}
            className="w-1.5 h-1.5 rounded-full"
          />
        ))}
      </motion.div>
    </div>
  );
};

// --- Page 4: Dashboard Charts ---
const DashboardChartsPage = () => {
  console.log(`[${ts()}] [DashboardChartsPage] Component rendered`);
  const [currentMetricIndex, setCurrentMetricIndex] = useState(0);
  const days = Array.from({ length: 7 }, (_, i) => i);
  const weeks = Array.from({ length: 45 }, (_, i) => i); // 45 weeks = 3x the original 15 weeks

  const metrics = [
    { emoji: "🔥", name: "Calories", color: "from-orange-500 to-red-500" },
    { emoji: "🍗", name: "Protein", color: "from-amber-500 to-orange-500" },
    { emoji: "💪", name: "Gym Time", color: "from-purple-500 to-pink-500" },
    { emoji: "💧", name: "Water", color: "from-blue-500 to-cyan-500" },
    { emoji: "😴", name: "Sleep", color: "from-indigo-500 to-purple-500" },
    { emoji: "🏃", name: "Run Distance", color: "from-green-500 to-emerald-500" },
  ];

  // Cycle through metrics every 3 seconds
  useEffect(() => {
    console.log(`[${ts()}] [DashboardChartsPage] Setting up metric cycle interval`);
    const interval = setInterval(() => {
      setCurrentMetricIndex((prev) => {
        const next = (prev + 1) % metrics.length;
        console.log(`[${ts()}] [DashboardChartsPage] Cycling to metric:`, next, metrics[next].name);
        return next;
      });
    }, 3000);
    return () => {
      console.log(`[${ts()}] [DashboardChartsPage] Cleaning up metric cycle interval`);
      clearInterval(interval);
    };
  }, [metrics.length]);

  const currentMetric = metrics[currentMetricIndex];

  const getIntensity = (week: number, day: number) => {
    const value = (week * 7 + day + Math.random() * 30 + currentMetricIndex * 50) % 110;
    if (value >= 100) return 4;
    if (value >= 75) return 3;
    if (value >= 50) return 2;
    if (value >= 25) return 1;
    return 0;
  };

  const getColor = (intensity: number) => {
    const colors = [
      "bg-[hsl(var(--contrib-0))]",
      "bg-[hsl(var(--contrib-1))]",
      "bg-[hsl(var(--contrib-2))]",
      "bg-[hsl(var(--contrib-3))]",
      "bg-[hsl(var(--contrib-4))]",
    ];
    return colors[intensity];
  };

  return (
    <div className="space-y-3 w-full h-full flex flex-col justify-center">
      <AnimatePresence mode="wait">
        <motion.div
          key={currentMetric.name}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
        >
          <Card className="p-3 sm:p-4 bg-gradient-to-br from-background to-accent/5 border-accent/20">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.08 }}
              className="flex items-center gap-2 mb-2"
            >
              <motion.span
                animate={{ scale: [1, 1.2, 1] }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                className="text-xl"
              >
                {currentMetric.emoji}
              </motion.span>
              <h4 className="font-medium text-sm sm:text-base">{currentMetric.name}</h4>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scaleX: 0 }}
              animate={{ opacity: 1, scaleX: 1 }}
              transition={{ delay: 0.15, duration: 0.4, ease: "easeOut" }}
              className="flex gap-0.5 justify-start overflow-x-auto origin-left will-change-transform"
            >
              {weeks.map((week) => (
                <div key={week} className="flex flex-col gap-0.5">
                  {days.map((day) => {
                    const intensity = getIntensity(week, day);
                    return (
                      <motion.div
                        key={day}
                        initial={{ opacity: 0, scale: 0 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{
                          delay: 0.2 + (week * 7 + day) * 0.001,
                          duration: 0.15,
                          type: "spring",
                          stiffness: 450,
                          damping: 28,
                        }}
                        whileHover={{
                          scale: 2,
                          zIndex: 10,
                          boxShadow: "0 0 10px rgba(var(--accent), 0.5)",
                        }}
                        className={`w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-[1px] ${getColor(intensity)} cursor-pointer transform-gpu will-change-transform`}
                      />
                    );
                  })}
                </div>
              ))}
            </motion.div>
          </Card>
        </motion.div>
      </AnimatePresence>
      
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
        className="text-xs text-center text-muted-foreground mt-2"
      >
        🔒 Control visibility in Settings → Profile
      </motion.p>
    </div>
  );
};

// --- Page 5: Commitment Pledges ---
const PenaltiesPage = () => {
  const { t } = useTranslation();
  console.log(`[${ts()}] [PenaltiesPage] Component rendered`);
  const [currentExample, setCurrentExample] = useState(0);
  const [visibleWords, setVisibleWords] = useState(0);
  const [hasShownLowerSections, setHasShownLowerSections] = useState(false);
  
  // Different accountability examples with modular variables
  const examples = [
    {
      words: [
        { text: "Unless", highlight: false },
        { text: "I", highlight: false },
        { text: "🏃 run 5 miles", highlight: true, isVariable: true },
        { text: "I'll donate", highlight: false },
        { text: "$50", highlight: true, isVariable: true },
        { text: "to", highlight: false },
        { text: "charity", highlight: false },
      ]
    },
    {
      words: [
        { text: "Unless", highlight: false },
        { text: "I", highlight: false },
        { text: "🍗 hit 150g protein,", highlight: true, isVariable: true },
        { text: "I'll donate", highlight: false },
        { text: "$25", highlight: true, isVariable: true },
        { text: "to", highlight: false },
        { text: "charity", highlight: false },
      ]
    },
    {
      words: [
        { text: "Unless", highlight: false },
        { text: "I", highlight: false },
        { text: "🎸 practice guitar 20 minutes,", highlight: true, isVariable: true },
        { text: "I'll donate", highlight: false },
        { text: "$15", highlight: true, isVariable: true },
        { text: "to", highlight: false },
        { text: "charity", highlight: false },
      ]
    },
    {
      words: [
        { text: "Unless", highlight: false },
        { text: "I", highlight: false },
        { text: "💻 push 3 GitHub commits,", highlight: true, isVariable: true },
        { text: "I'll donate", highlight: false },
        { text: "$40", highlight: true, isVariable: true },
        { text: "to", highlight: false },
        { text: "charity", highlight: false },
      ]
    },
  ];

  const currentWords = examples[currentExample].words;

  // Word-by-word animation for current example
  useEffect(() => {
    console.log(`[${ts()}] [PenaltiesPage] Starting word animation for example:`, currentExample);
    setVisibleWords(0);
    
    const interval = setInterval(() => {
      setVisibleWords((prev) => {
        if (prev >= currentWords.length) {
          clearInterval(interval);
          return prev;
        }
        return prev + 1;
      });
    }, 200); // Each word appears every 200ms

    return () => {
      console.log(`[${ts()}] [PenaltiesPage] Cleaning up word animation interval`);
      clearInterval(interval);
    };
  }, [currentExample, currentWords.length]);

  // Cycle through examples after all words are shown
  useEffect(() => {
    if (visibleWords >= currentWords.length) {
      // Mark that lower sections should now be permanently visible
      setHasShownLowerSections(true);
      
      const timer = setTimeout(() => {
        setCurrentExample((prev) => {
          const next = (prev + 1) % examples.length;
          console.log(`[${ts()}] [PenaltiesPage] Cycling to example:`, next);
          return next;
        });
      }, 2500); // Wait 2.5s before showing next example
      
      return () => clearTimeout(timer);
    }
  }, [visibleWords, currentWords.length, examples.length]);

  // Calculate total animation time for the text (words * 200ms + transition time)
  const textAnimationDuration = currentWords.length * 200 + 400;

  return (
    <div className="space-y-4 sm:space-y-6 w-full max-w-md mx-auto h-full flex flex-col justify-center">
      {/* Consequence card with word-by-word animation */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentExample}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          onAnimationStart={() => console.log(`[${ts()}] [PenaltiesPage] Card animation started for example:`, currentExample)}
          onAnimationComplete={() => console.log(`[${ts()}] [PenaltiesPage] Card animation completed for example:`, currentExample)}
          transition={{ duration: 0.4 }}
          className="w-full bg-card rounded-xl border border-destructive/20 p-4 sm:p-6 shadow-lg bg-gradient-to-br from-destructive/5 to-destructive/10"
        >
          <div className="flex items-center justify-center gap-2 text-base sm:text-lg font-medium text-foreground flex-wrap min-h-[80px] sm:min-h-[100px]">
            {currentWords.slice(0, visibleWords).map((word, idx) => (
              <motion.span
                key={`${currentExample}-${idx}`}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ 
                  opacity: 1, 
                  scale: 1,
                  // Add extra emphasis to variable words
                  ...(word.isVariable && {
                    textShadow: ["0 0 0px rgba(239, 68, 68, 0)", "0 0 8px rgba(239, 68, 68, 0.4)", "0 0 0px rgba(239, 68, 68, 0)"]
                  })
                }}
                transition={{ 
                  duration: 0.3,
                  ...(word.isVariable && {
                    textShadow: {
                      duration: 1.5,
                      repeat: Infinity,
                      ease: "easeInOut",
                      delay: 0.5
                    }
                  })
                }}
                className={`${word.highlight ? "text-destructive font-bold" : ""} ${word.isVariable ? "relative" : ""}`}
              >
                {word.text}
                {word.isVariable && (
                  <motion.div
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: 1 }}
                    transition={{ delay: 0.3, duration: 0.3 }}
                    className="absolute -bottom-0.5 left-0 right-0 h-0.5 bg-destructive/40 rounded-full origin-left"
                  />
                )}
              </motion.span>
            ))}
          </div>
        </motion.div>
      </AnimatePresence>

      {/* "Completely optional" - appears after text animation completes, stays visible */}
      {hasShownLowerSections && (
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 0.7, y: 0 }}
          transition={{ duration: 0.4 }}
          className="text-xs text-center text-muted-foreground"
        >
          ✨ Completely optional
        </motion.p>
      )}

      {/* Perks Section - appears after "optional" text, remains visible once shown */}
      {hasShownLowerSections && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.4 }}
          className="space-y-3"
        >
          {/* Donation Badge Preview */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.4 }}
            className="flex flex-col items-center gap-2"
          >
            <div className="flex items-center gap-2 px-3 py-2 border border-border/50 rounded-lg bg-background/40">
              <p className="text-[11px] font-medium text-muted-foreground border border-border/50 rounded-full px-2 py-0.5 bg-background/40">
                💝 $100 donated
              </p>
              <span className="text-xs text-muted-foreground/60">{t('ui.text.badge_appears_on_profile')}</span>
            </div>
          </motion.div>

          {/* Discord Live Feed */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.4 }}
            className="flex flex-col items-center gap-2"
          >
            <Button
              variant="outline"
              size="sm"
              className="text-xs hover:text-primary gap-1.5 border-primary/30 hover:border-primary/50 mt-1"
              onClick={() => openInExternalBrowser("https://discord.gg/FaYD2mJFhW")}
            >
              <Rss className="w-3.5 h-3.5" />
              📢 Live punishment feed on Discord!
            </Button>
          </motion.div>
        </motion.div>
      )}

      {/* Progress Dots - appears last, remains visible once shown */}
      {hasShownLowerSections && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.1, duration: 0.3 }}
          className="flex justify-center gap-1.5"
        >
          {examples.map((_, idx) => (
            <motion.div
              key={idx}
              animate={{
                scale: currentExample === idx ? 1.2 : 1,
                backgroundColor:
                  currentExample === idx
                    ? "hsl(var(--destructive))"
                    : "hsl(var(--muted-foreground) / 0.3)",
              }}
              transition={{ duration: 0.3 }}
              className="w-1.5 h-1.5 rounded-full"
            />
          ))}
        </motion.div>
      )}
    </div>
  );
};
